import type { TranscriptionSegment } from '../api/transcriptions';
import type { ExportOptions } from '../components/ExportTranscript';
import { stripKnownTranscriptionTitleExtension } from './transcriptionTitle';

// --- Timestamp Formatting ---

// Formats seconds into HH:MM:SS,mmm (SubRip format)
function formatTimestampSRT(seconds: number): string {
  const date = new Date(0);
  date.setUTCMilliseconds(seconds * 1000);
  const iso = date.toISOString(); // "YYYY-MM-DDTHH:mm:ss.sssZ"
  // Extract "HH:mm:ss.sss" and replace dot with comma
  return iso.substr(11, 12).replace('.', ',');
}

// Formats seconds into HH:MM:SS.mmm (WebVTT format)
function formatTimestampVTT(seconds: number): string {
  const date = new Date(0);
  date.setUTCMilliseconds(seconds * 1000);
  const iso = date.toISOString();
  return iso.substr(11, 12);
}

// Formats seconds into [MM:SS] or [HH:MM:SS] for text/markdown
function formatTimestampText(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `[${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}]`;
  }
  return `[${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}]`;
}


// --- Format Generators ---

export function generateTXT(segments: TranscriptionSegment[], options: ExportOptions): string {
  return segments.map(segment => {
    let line = '';
    
    if (options.includeTimecodes) {
      line += `${formatTimestampText(segment.start)} `;
    }
    
    if (options.includeSpeakerNames && segment.speaker) {
      line += `${segment.speaker}: `;
    }
    
    line += segment.text;
    return line;
  }).join('\n\n');
}

export function generateMarkdown(segments: TranscriptionSegment[], options: ExportOptions): string {
  return segments.map(segment => {
    let line = '';
    
    if (options.includeTimecodes) {
      line += `\`${formatTimestampText(segment.start)}\` `;
    }
    
    if (options.includeSpeakerNames && segment.speaker) {
      line += `**${segment.speaker}**: `;
    }
    
    line += segment.text;
    return line;
  }).join('\n\n');
}

export function generateJSON(segments: TranscriptionSegment[]): string {
  return JSON.stringify(segments, null, 2);
}

export function generateSRT(segments: TranscriptionSegment[]): string {
  return segments.map((segment, index) => {
    const sequenceNumber = index + 1;
    const timeRange = `${formatTimestampSRT(segment.start)} --> ${formatTimestampSRT(segment.end)}`;
    return `${sequenceNumber}\n${timeRange}\n${segment.text}\n`;
  }).join('\n');
}

export function generateVTT(segments: TranscriptionSegment[]): string {
  const header = 'WEBVTT\n\n';
  const body = segments.map(segment => {
    const timeRange = `${formatTimestampVTT(segment.start)} --> ${formatTimestampVTT(segment.end)}`;
    // Optional: Add speaker name in VTT format if available
    const text = segment.speaker ? `<v ${segment.speaker}>${segment.text}` : segment.text;
    return `${timeRange}\n${text}\n`;
  }).join('\n');
  
  return header + body;
}

// --- Blob & Download ---

export function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildExportFilename(displayName: string, extension: string, stripTitleExtension = true) {
  const stripped = stripTitleExtension ? stripKnownTranscriptionTitleExtension(displayName) : '';
  const baseName = (stripped || displayName).trim() || 'transcription';
  const sanitized = baseName.replace(/[\\/:"*?<>|]+/g, '-');
  const normalized = sanitized.replace(/\s+/g, ' ').trim();
  return `${normalized}-transcription.${extension}`;
}
